import {
  createEffect,
  createMemo,
  createResource,
  Match,
  Show,
  Switch,
  untrack,
  onCleanup,
  type ParentProps,
} from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"
import { useLocation, useNavigate } from "@solidjs/router"
import { IconButton } from "@opencode/ui/icon-button"
import { Icon } from "@opencode/ui/icon"
import { Tooltip } from "@opencode/ui/tooltip"

import { LayoutRoute, useLayout } from "@/shell/state/layout"
import { usePlatform } from "@/runtime/platform/platform"
import { useCommand } from "@/shell/commands/command"
import { useLanguage } from "@/runtime/i18n/language"
import { useSettings } from "@/settings/model"
import { useSettingsSurface } from "@/settings/surface"
import { WindowsAppMenu } from "./windows-menu"
import { createTitlebarHistory } from "./history-controller"
import { makeEventListener } from "@solid-primitives/event-listener"
import { createResizeObserver } from "@solid-primitives/resize-observer"
import { createMediaQuery } from "@solid-primitives/media"
import { readSessionTabsRemovedDetail, SESSION_TABS_REMOVED_EVENT } from "@/shell/titlebar/session-events"
import { useGlobal } from "@/runtime/server/runtime"
import { ServerConnection } from "@/runtime/server/registry"
import { tabKey, useTabs } from "@/shell/tabs/tabs"
import type { ComposerState } from "@/composer/persistence"
import "./titlebar.css"
import { TitlebarRightMount } from "@/shell/titlebar/right-slot"
import { SairNavigation } from "@/shell/navigation"
import { sessionTabTitle } from "./tab-title"
import { updaterAction } from "@/shell/updates/action"
import type { UpdaterState } from "@/shell/updates/types"

const titlebarHeight = 48
const windowsTitlebarHeight = 44 // Includes the content inset; matches the native Windows overlay.
const minTitlebarZoom = 0.25
const windowsControlsBaseWidth = 138 // 3 native Windows caption buttons at 46px each.

export type TitlebarUpdate = {
  state: UpdaterState | undefined
  install: () => void
}

export function Titlebar(props: {
  update?: TitlebarUpdate
  navigation?: { mount?: HTMLElement; expanded: boolean; compact: boolean; onToggle: () => void }
}) {
  let element: HTMLElement | undefined
  createResizeObserver(
    () => element,
    () => {
      if (!element) return
      document.documentElement.style.setProperty(
        "--sair-titlebar-height",
        `${element.getBoundingClientRect().height}px`,
      )
    },
  )
  onCleanup(() => document.documentElement.style.removeProperty("--sair-titlebar-height"))
  const platform = usePlatform()
  const command = useCommand()
  const language = useLanguage()
  const settings = useSettings()
  const settingsSurface = useSettingsSurface()
  const navigate = useNavigate()
  const location = useLocation()
  const mobile = createMediaQuery("(max-width: 767px)")
  const bottom = createMemo(() => mobile() && settings.general.mobileTitlebarPosition() === "bottom")

  const mac = createMemo(() => platform.platform === "desktop" && platform.os === "macos")
  const windows = createMemo(() => platform.platform === "desktop" && platform.os === "windows")
  const linux = createMemo(() => platform.platform === "desktop" && platform.os === "linux")
  const zoom = () => platform.webviewZoom?.() ?? 1
  const titlebarZoom = () => (windows() ? Math.max(zoom(), minTitlebarZoom) : zoom())
  const minHeight = () => {
    if (mac()) return `${titlebarHeight / zoom()}px`
    if (windows()) return `env(titlebar-area-height, ${windowsTitlebarHeight / Math.min(titlebarZoom(), 1)}px)`
    return undefined
  }
  const windowsControlsWidth = () => `${windowsControlsBaseWidth / Math.max(titlebarZoom(), 1)}px`
  const windowsTitlebarWidth = () =>
    `calc(env(titlebar-area-width, calc(100vw - ${windowsControlsWidth()})) - var(--sair-navigation-width))`

  const history = createTitlebarHistory()
  const path = () => `${location.pathname}${location.search}${location.hash}`

  const updateState = createMemo<TitlebarUpdatePillState>(() => {
    const state = props.update?.state
    const installing = state?.status === "installing"
    const version = state?.status === "ready" || state?.status === "download-required" ? state.version : undefined
    return {
      visible: version !== undefined || installing,
      installing,
      label: language.t("titlebar.update"),
      ariaLabel: language.t(updaterAction(state).label),
      title: version ? language.t("titlebar.updateVersion", { version }) : undefined,
      onInstall: () => props.update?.install(),
    }
  })
  const rightState = createMemo<TitlebarRightState>(() => ({
    update: updateState(),
  }))

  command.register(() => [
    {
      id: "common.goBack",
      title: language.t("common.goBack"),
      category: language.t("command.category.view"),
      keybind: "mod+[",
      onSelect: () => {
        if (history.state.index > 0) {
          history.back()
          return
        }
        if (settingsSurface.active()) settingsSurface.returnToSource()
      },
    },
    {
      id: "common.goForward",
      title: language.t("common.goForward"),
      category: language.t("command.category.view"),
      keybind: "mod+]",
      onSelect: history.forward,
    },
  ])

  return (
    <header
      ref={(node) => {
        element = node
      }}
      data-slot="titlebar-v2"
      classList={{
        "shrink-0 relative flex flex-row h-12 bg-v2-background-bg-base overflow-visible": true,
        "order-last": bottom(),
      }}
      style={{
        height:
          platform.platform === "web"
            ? bottom()
              ? "calc(48px + max(0px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))))"
              : "calc(48px + max(0px, env(safe-area-inset-top, 0px)))"
            : undefined,
        "padding-top": bottom() ? "0px" : "env(safe-area-inset-top, 0px)",
        "padding-bottom": bottom() ? "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))" : "0px",
        "min-height": minHeight(),
        width: windows() ? windowsTitlebarWidth() : undefined,
        "max-width": windows() ? windowsTitlebarWidth() : undefined,
        // Native Windows caption controls remain on the physical right in both writing directions.
        "margin-right": windows() ? "auto" : undefined,
      }}
      data-tauri-drag-region
    >
      <Switch>
        <Match when>
          {(_) => {
            const layout = useLayout()
            const global = useGlobal()

            const tabs = useTabs()
            const tabsStore = tabs.store
            const tabsStoreActions = tabs
            const preparing = createMemo(() => {
              const route = layout.route()
              return route.type === "session" && !!tabs.pendingSession(route.server, route.sessionId)
            })
            const [loadedSession] = createResource(
              () => {
                const route = layout.route()
                if (route.type !== "session") return undefined
                if (preparing()) return undefined
                const conn = global.servers.list().find((item) => ServerConnection.key(item) === route.server)
                return conn ? { route, ctx: global.ensureServerCtx(conn) } : undefined
              },
              ({ route, ctx }) => ctx.sdk.api.session.get({ sessionID: route.sessionId }).catch(() => {}),
            )
            const session = createMemo(() => {
              const route = layout.route()
              if (route.type !== "session") return
              if (preparing()) return
              const conn = global.servers.list().find((item) => ServerConnection.key(item) === route.server)
              const cached = conn ? global.ensureServerCtx(conn).data.session.get(route.sessionId) : undefined
              if (cached) return cached
              const loaded = loadedSession()
              return loaded?.id === route.sessionId ? loaded : undefined
            })

            const matchRoute = (route: LayoutRoute) => {
              if (route.type === "home") return
              if (route.type === "draft") {
                return tabsStore.find((item) => item.type === "draft" && item.draftID === route.draftID)
              }
              if (route.type === "session") {
                const main = tabsStore.find(
                  (item) =>
                    item.type === "session" &&
                    item.server === route.server &&
                    (item.sessionId === route.sessionId || item.routeSessionId === route.sessionId),
                )
                if (main) return main
                const s = session()
                if (s?.parentID) {
                  const parentID = s.parentID
                  const parent = tabsStore.find(
                    (item) => item.type === "session" && item.server === route.server && item.sessionId === parentID,
                  )
                  if (parent) return parent
                }
              }
            }

            const currentTab = () => matchRoute(layout.route())

            createEffect(() => {
              const route = layout.route()
              if (!tabs.ready()) return
              const tab = currentTab()
              if (tab) {
                const current = session()
                if (
                  route.type === "session" &&
                  tab.type === "session" &&
                  (route.sessionId === tab.sessionId || current?.id === route.sessionId)
                ) {
                  tabs.rememberSessionRoute(tab, route.sessionId, current?.parentID)
                }
                tabs.remember(tab)
                return
              }

              if (route.type === "session") {
                if (tabs.pendingSession(route.server, route.sessionId)) {
                  tabsStoreActions.addSessionTab({ server: route.server, sessionId: route.sessionId })
                  return
                }
                const s = session()
                if (!s) return
                const sessionId = s.parentID ?? s.id
                const next = { server: route.server, sessionId }
                tabsStoreActions.addSessionTab(next)
              }
            })

            makeEventListener(window, SESSION_TABS_REMOVED_EVENT, (event) => {
              const detail = readSessionTabsRemovedDetail(event)
              if (!detail) return
              tabsStoreActions.removeSessions(detail)
            })

            const openNewTab = () => {
              const route = layout.route()
              switch (route.type) {
                case "session": {
                  const pending = tabs.pendingSession(route.server, route.sessionId)
                  if (pending) {
                    const model = tabs.stateValue<ComposerState>(pending.draft, "prompt")?.model.current()
                    void tabs.newDraft(
                      {
                        server: route.server,
                        directory: pending.draft.directory,
                        pendingProject: pending.draft.pendingProject,
                      },
                      "",
                      model,
                    )
                    return
                  }
                  const activeSession = session()
                  if (!activeSession) return

                  const sessionTab = {
                    type: "session" as const,
                    server: route.server,
                    sessionId: activeSession.id,
                  }
                  const model = tabs.stateValue<ComposerState>(sessionTab, "prompt")?.model.current()
                  void tabs.newDraft(
                    { server: sessionTab.server, directory: activeSession.location.directory },
                    "",
                    model,
                  )
                  return
                }
                case "draft": {
                  const activeTab = currentTab()
                  if (activeTab?.type !== "draft") return

                  const model = tabs.stateValue<ComposerState>(activeTab, "prompt")?.model.current()
                  void tabs.newDraft(
                    {
                      server: activeTab.server,
                      directory: activeTab.directory,
                      pendingProject: activeTab.pendingProject,
                    },
                    "",
                    model,
                  )
                  return
                }
                case "new":
                  return
                case "settings":
                case "home": {
                  navigate("/new-session")
                }
              }
            }
            const toggleHome = () => tabs.toggleHome({ home: layout.route().type === "home", current: currentTab() })
            command.register("titlebar-home", () => [
              {
                id: "home.toggle",
                title: language.t("session.navigation.library"),
                category: language.t("command.category.view"),
                keybind: windows() ? "alt+home" : "mod+b",
                hidden: true,
                onSelect: toggleHome,
              },
            ])

            command.register("tabs", () => {
              const current = currentTab()

              return [
                {
                  id: "tab.new",
                  category: "tab",
                  title: language.t("command.session.new"),
                  keybind: "mod+t,mod+n",
                  hidden: true,
                  onSelect: openNewTab,
                },
                current && {
                  id: "tab.close",
                  category: "tab",
                  title: language.t("command.tab.close"),
                  keybind: "mod+w",
                  hidden: true,
                  onSelect: () => {
                    tabsStoreActions.closeTab(tabsStore.findIndex((tab) => current === tab))
                  },
                },
                {
                  id: "tab.reopenClosed",
                  category: language.t("command.category.file"),
                  title: language.t("command.tab.reopenClosed"),
                  keybind: "mod+shift+t",
                  onSelect: () => tabsStoreActions.reopenClosedTab(),
                },
              ].filter((v) => v !== undefined)
            })

            const [navigation, setNavigation] = createStore({ open: false })
            const setNavigationOpen = (open: boolean) => {
              if (open) window.dispatchEvent(new Event("sair:navigation-open"))
              setNavigation("open", open)
            }
            const toggleNavigation = () => {
              if (props.navigation?.compact) {
                setNavigationOpen(!navigation.open)
                return
              }
              props.navigation?.onToggle()
            }
            const browseSessions = () => {
              if (layout.route().type !== "home") toggleHome()
              requestAnimationFrame(() => {
                document.querySelector<HTMLInputElement>('[data-component="home-session-search"] input')?.focus()
              })
            }
            const currentTitle = () => {
              const tab = currentTab()
              if (layout.route().type === "settings") return language.t("sidebar.settings")
              if (layout.route().type === "new") return language.t("session.tab.session")
              if (!tab) return language.t("session.navigation.library")
              if (tab.type === "draft") return language.t("session.tab.session")
              const value = session()
              return sessionTabTitle(
                value ? value.title : tabs.info[tabKey(tab)]?.title,
                language.t("session.tab.session"),
              )
            }
            createEffect(() => {
              path()
              mobile()
              props.navigation?.expanded
              setNavigation("open", false)
            })

            return (
              <div class="sair-titlebar-content h-full min-w-0 flex-1 flex items-center gap-2 px-3">
                <Show when={props.navigation?.mount} keyed>
                  {(mount) => (
                    <Portal mount={mount}>
                      <SairNavigation
                        expanded={props.navigation?.expanded ?? false}
                        route={layout.route().type}
                        historyOpen={navigation.open}
                        onHistoryChange={setNavigationOpen}
                        onToggle={toggleNavigation}
                        onHome={browseSessions}
                        onNew={openNewTab}
                      />
                    </Portal>
                  )}
                </Show>
                <Show when={!props.navigation?.expanded}>
                  <Tooltip value={language.t("session.navigation.toggle")} placement="bottom" openDelay={200}>
                    <IconButton
                      class="sair-mobile-navigation-trigger"
                      data-action="sair-sidebar-toggle"
                      icon={<Icon name="layout-left" />}
                      variant="ghost-muted"
                      aria-label={language.t(
                        navigation.open ? "session.navigation.collapse" : "session.navigation.expand",
                      )}
                      aria-expanded={navigation.open}
                      onClick={(event) => {
                        toggleNavigation()
                        if (props.navigation?.compact || event.detail !== 0) return
                        requestAnimationFrame(() => {
                          document.querySelector<HTMLElement>('[data-action="sair-collapse-navigation"]')?.focus()
                        })
                      }}
                    />
                  </Tooltip>
                </Show>
                <Show when={windows() || linux()}>
                  <WindowsAppMenu command={command} platform={platform} />
                </Show>
                <TitlebarRight state={rightState()}>
                  <div data-slot="sair-titlebar-fallback" class="flex min-w-0 flex-1 items-center">
                    <span class="truncate text-[13px] leading-4 text-v2-text-text-base">{currentTitle()}</span>
                  </div>
                </TitlebarRight>
              </div>
            )
          }}
        </Match>
      </Switch>
    </header>
  )
}

type TitlebarUpdatePillState = {
  visible: boolean
  installing: boolean
  label: string
  ariaLabel: string
  title?: string
  onInstall: () => void
}

type TitlebarRightState = {
  update: TitlebarUpdatePillState
}

function TitlebarRight(props: ParentProps<{ state: TitlebarRightState }>) {
  return (
    <div class="relative z-20 flex min-w-0 flex-1 items-center justify-end gap-0 overflow-visible">
      <Show when={props.state.update.visible}>
        <TitlebarUpdateIconButton state={props.state.update} />
      </Show>
      <TitlebarRightMount>{props.children}</TitlebarRightMount>
    </div>
  )
}

function TitlebarUpdateIconButton(props: { state: TitlebarUpdatePillState; vertical?: boolean }) {
  const label = () => (
    <span
      class="shrink-0 text-[11px] leading-4 text-v2-text-text-accent [font-weight:530] opacity-0 motion-safe:transition-all duration-150 ease-out group-hover:opacity-100 group-hover:translate-x-0 group-focus-within:opacity-100 group-focus-within:translate-x-0 motion-reduce:translate-x-0"
      classList={{
        "ms-px me-4 -translate-x-2 rtl:translate-x-2": props.vertical,
        "ms-2 me-px translate-x-2 rtl:-translate-x-2": !props.vertical,
      }}
    >
      {props.state.label}
    </span>
  )
  return (
    <div
      data-slot="titlebar-update"
      class="group relative shrink-0 rounded-full bg-v2-background-bg-deep transition-[width] duration-150 ease-out hover:z-30 focus-within:z-30 motion-reduce:transition-none"
      classList={{
        "h-7 w-7 self-start hover:w-[84px] focus-within:w-[84px]": props.vertical,
        "me-3 h-5 w-5 hover:w-[68px] focus-within:w-[68px]": !props.vertical,
      }}
    >
      <button
        type="button"
        class="absolute top-0 z-10 flex h-full w-full items-center overflow-hidden rounded-full bg-v2-icon-icon-accent/20 text-v2-icon-icon-accent transition-[background-color] duration-150 ease-out group-hover:bg-[color-mix(in_srgb,var(--v2-icon-icon-accent)_20%,var(--v2-background-bg-deep))] group-focus-within:bg-[color-mix(in_srgb,var(--v2-icon-icon-accent)_20%,var(--v2-background-bg-deep))] focus-visible:outline-none disabled:opacity-60 motion-reduce:transition-none [app-region:no-drag]"
        classList={{ "start-0 justify-start": props.vertical, "end-0 justify-end": !props.vertical }}
        onClick={props.state.onInstall}
        disabled={props.state.installing}
        aria-busy={props.state.installing}
        aria-label={props.state.ariaLabel}
      >
        <Show when={!props.vertical}>{label()}</Show>
        <span
          class="flex shrink-0 items-center justify-center"
          classList={{ "size-7": props.vertical, "size-5": !props.vertical }}
        >
          <Show
            when={!props.state.installing}
            fallback={<span data-slot="titlebar-update-loader" aria-hidden="true" />}
          >
            <Icon name="arrow-down" />
          </Show>
        </span>
        <Show when={props.vertical}>{label()}</Show>
      </button>
    </div>
  )
}
